import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { Return } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useUpdateReturn } from '../hooks/use-return-mutations';
import {
  fromReturnDetails,
  returnDetailsSchema,
  toReturnDetailsPatch,
  type ReturnDetailsForm,
} from '../schemas/return-details-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['staffNote', 'returnShippingPaidBy']);

/** The shopper's note (read-only), the staff note and who pays the return label. */
export function ReturnDetailsCard({
  storeId,
  ret,
  readOnly,
}: {
  storeId: string;
  ret: Return;
  readOnly: boolean;
}) {
  const { t } = useTranslation(['returns', 'common']);
  const format = useFormat();
  const update = useUpdateReturn(storeId, 'returns:toasts.detailsSaved');
  const message = useErrorMessage();
  const form = useForm<ReturnDetailsForm>({
    resolver: zodResolver(returnDetailsSchema),
    defaultValues: fromReturnDetails(ret),
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync({ id: ret.id, body: toReturnDetailsPatch(values) });
      form.reset(values);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });
  useSaveBar({ form, onSave: submit, saving: update.isPending, disabled: readOnly });

  return (
    <FormSection title={t('returns:details.title')}>
      <dl className="grid gap-3 text-sm">
        <div>
          <dt className="text-muted-foreground">{t('returns:details.reason')}</dt>
          <dd className="whitespace-pre-line">{ret.reason ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('returns:details.customerNote')}</dt>
          <dd className="whitespace-pre-line" data-testid="return-customer-note">
            {ret.customerNote ?? t('returns:details.none')}
          </dd>
        </div>
        {ret.restockingFee.amount > 0 ? (
          <div>
            <dt className="text-muted-foreground">{t('returns:details.restockingFee')}</dt>
            <dd className="tabular-nums">{format.money(ret.restockingFee)}</dd>
          </div>
        ) : null}
        {ret.resolvedAt ? (
          <div>
            <dt className="text-muted-foreground">{t('returns:details.resolvedAt')}</dt>
            <dd title={format.utc(ret.resolvedAt)}>{format.dateTime(ret.resolvedAt)}</dd>
          </div>
        ) : null}
      </dl>
      <Form {...form}>
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          <FormField
            control={form.control}
            name="returnShippingPaidBy"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('returns:details.shippingPaidBy')}</FormLabel>
                <Select value={field.value} onValueChange={field.onChange} disabled={readOnly}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="customer">{t('returns:paidBy.customer')}</SelectItem>
                    <SelectItem value="store">{t('returns:paidBy.store')}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="staffNote"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('returns:details.staffNote')}</FormLabel>
                <FormControl>
                  <Textarea rows={3} disabled={readOnly} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {readOnly ? null : (
            <div>
              <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
                {update.isPending ? t('common:actions.saving') : t('common:actions.save')}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </FormSection>
  );
}
